import { cva, type VariantProps } from 'class-variance-authority';
import { InfoIcon, type LucideProps } from 'lucide-react';
import * as React from 'react';
import { AiIcon } from '../../assets/icons/ai-icon.tsx';
import { cn } from '../../lib/utils.ts';
import Text from './text';
import { TooltipInfo } from './tooltip';

export type NoticeVariant = 'default' | 'warning' | 'info' | 'ai';

const noticeVariants = cva('flex flex-col sm:flex-row rounded-md border px-3 py-1.5 text-sm font-normal', {
    variants: {
        variant: {
            default: 'bg-neutral-50 text-neutral-500 border-neutral-100 ',
            warning: 'bg-red-50 text-red-600 border-red-300 ',
            info: 'bg-blue-50 text-blue-400 border-blue-300 ',
            ai: 'border-0 bg-linear-to-r from-[#4c74e61a] to-[#8d54e91a] text-neutral-700',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

export type NoticeProps = {
    /** Intent variant */
    variant: NoticeVariant;

    /** Optional heading text */
    title?: React.ReactNode;

    /** Description text or content */
    description?: React.ReactNode;

    /** Additional CSS class(es) for styling */
    className?: string;

    icon?: React.ComponentType<LucideProps>;

    withTooltip?: boolean;

    action?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> &
    VariantProps<typeof noticeVariants>;

export const Notice = React.forwardRef<HTMLDivElement, NoticeProps>(
    (
        {
            variant,
            title,
            description,
            className,
            icon: IconComponent = InfoIcon,
            withTooltip,
            action,
            ...props
        },
        ref,
    ) => {
        return (
            <div ref={ref} className={cn(noticeVariants({ variant }), className)} {...props}>
                <div className="flex items-start gap-1 grow">
                    <div className="flex items-center min-h-5">
                        {variant === 'ai' ? <AiIcon /> : <IconComponent size={16} />}
                    </div>
                    <div
                        className={cn('grow', {
                            'flex items-center gap-1': withTooltip,
                        })}
                    >
                        {title && (
                            <Text weight="semibold" color="neutral-800">
                                {title}&nbsp;
                            </Text>
                        )}
                        {!withTooltip && description && <Text color="neutral-700">{description}</Text>}
                        {withTooltip && <TooltipInfo>{description}</TooltipInfo>}
                    </div>
                </div>
                {action && <div className="flex">{action}</div>}
            </div>
        );
    },
);

Notice.displayName = 'Notice';
